import { h, createContext } from 'preact';
import { useState, useEffect, useContext, useCallback } from 'preact/hooks';
import { useShopware } from '../shopware/ShopwareContext';
import { useEnsureShopwareLogin } from '../shopware/useEnsureShopwareLogin';
import type { Order as ShopwareOrder } from '../shopware/shopwareApi';
import { fetchPacksizeDetails, type PacksizeDetails } from '../utils/fetchPacksizeDetails';

const ITEMS_PER_PAGE = 5;

// Date range options
const dateRangeOptions = [
  { value: '', label: 'Alle Zeiträume' },
  { value: 'last-month', label: 'Letzter Monat' },
  { value: 'last-three-months', label: 'Letzte 3 Monate' },
  { value: 'last-year', label: 'Letztes Jahr' },
];

// Helper function to build date filter for Shopware API
const buildDateFilter = (range: string): any[] => {
  if (!range) return [];

  const now = new Date();
  const cutoffDate = new Date();
  let days = 0;

  switch (range) {
    case 'last-month':
      days = 30;
      break;
    case 'last-three-months':
      days = 90;
      break;
    case 'last-year':
      days = 365;
      break;
    default:
      return [];
  }

  cutoffDate.setDate(now.getDate() - days);

  return [
    {
      type: 'range',
      field: 'orderDateTime',
      parameters: {
        gte: cutoffDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
      },
    },
  ];
};

interface OrdersContextValue {
  // Data
  orders: ShopwareOrder[];
  dateRangeOptions: typeof dateRangeOptions;

  // Loading states
  isLoading: boolean;
  hasError: boolean;

  // Pagination
  currentPage: number;
  totalPages: number;
  totalCount: number;
  itemsPerPage: number;

  // Filters & Sort
  sortOrder: string;
  dateRange: string;
  hasActiveFilters: boolean;

  // Packsize details (points, dosageFormName, size)
  detailsMap: Record<string, PacksizeDetails | null>;
  isLoadingDetails: boolean;
  hasDetailsError: boolean;
  getOrderTotalPoints: (order: ShopwareOrder) => number;
  getLineItemDetails: (oNumber: string) => PacksizeDetails | null;

  // Actions
  handleSortChange: (sort: string) => void;
  handleDateRangeChange: (range: string) => void;
  handleResetFilters: () => void;
  handlePageChange: (page: number) => void;
}

const OrdersContext = createContext<OrdersContextValue | null>(null);

interface OrdersProviderProps {
  children: preact.ComponentChildren;
}

export const OrdersProvider = ({ children }: OrdersProviderProps) => {
  const { fetchOrders } = useShopware();

  // State
  const [sortOrder, setSortOrder] = useState<string>('newest');
  const [dateRange, setDateRange] = useState<string>('');
  const [shopwareOrders, setShopwareOrders] = useState<ShopwareOrder[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState<boolean>(true); // Start as true to show loading state immediately
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasError, setHasError] = useState<boolean>(false);

  // Packsize details state (points, dosageFormName, size)
  const [detailsMap, setDetailsMap] = useState<Record<string, PacksizeDetails | null>>({});
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(false);
  const [hasDetailsError, setHasDetailsError] = useState<boolean>(false);

  // Stable error handler to avoid unnecessary re-runs of useEnsureShopwareLogin
  const handleLoginError = useCallback((error: Error) => {
    console.error('[OrdersContext] Login failed:', error);
    setHasError(true);
  }, []);

  // Ensure user is logged in to Shopware before fetching orders
  const { isLoggedIn, loginError } = useEnsureShopwareLogin({
    onError: handleLoginError,
  });

  // Stop loading if login failed
  useEffect(() => {
    if (loginError) {
      setIsLoadingOrders(false);
    }
  }, [loginError]);

  // Fetch orders with server-side pagination, sorting, and filtering
  useEffect(() => {
    if (!isLoggedIn) return;

    let isCancelled = false;

    // Helper to build request body based on current filters/sort/pagination
    const buildRequestBody = () => {
      const filters: any[] = buildDateFilter(dateRange);
      const sort = [
        {
          field: 'orderDateTime',
          order: sortOrder === 'oldest' ? 'ASC' : 'DESC',
        },
      ];

      return {
        'total-count-mode': 'exact' as const,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        ...(filters.length > 0 && { filter: filters }),
        sort,
        associations: {
          lineItems: {
            associations: {
              cover: {},
            },
          },
          documents: {},
          deliveries: {
            associations: {
              stateMachineState: {},
            },
          },
          stateMachineState: {},
        },
      };
    };

    const loadOrders = async () => {
      setIsLoadingOrders(true);
      try {
        const result = await fetchOrders(buildRequestBody());

        if (isCancelled) return;

        setShopwareOrders(result.elements || []);
        setTotalCount(result.total || 0);
        setHasError(false);
      } catch (error) {
        if (isCancelled) return;

        console.error('[OrdersContext] Failed to fetch orders:', error);
        setShopwareOrders([]);
        setTotalCount(0);
        setHasError(true);
      } finally {
        if (!isCancelled) {
          setIsLoadingOrders(false);
        }
      }
    };

    loadOrders();

    // Cleanup function to cancel stale requests
    return () => {
      isCancelled = true;
    };
  }, [fetchOrders, currentPage, sortOrder, dateRange, isLoggedIn]);

  // Initialize state from URL params on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const urlSort = urlParams.get('sort');
    if (urlSort === 'newest' || urlSort === 'oldest') {
      setSortOrder(urlSort);
    }

    const urlDateRange = urlParams.get('dateRange');
    if (urlDateRange !== null) {
      setDateRange(urlDateRange);
    }

    const urlPage = urlParams.get('page');
    if (urlPage) {
      const pageNum = parseInt(urlPage, 10);
      if (!isNaN(pageNum) && pageNum > 0) {
        setCurrentPage(pageNum);
      }
    }
  }, []);

  // Fetch packsize details for all oNumbers from current orders
  useEffect(() => {
    if (shopwareOrders.length === 0) {
      setDetailsMap({});
      setIsLoadingDetails(false);
      return;
    }

    // Extract all unique oNumbers from line items (productNumber is the oNumber)
    const allONumbers: string[] = [];
    for (const order of shopwareOrders) {
      const lineItems = order.lineItems || [];
      for (const item of lineItems) {
        const productNumber = item.payload?.productNumber;
        if (productNumber && typeof productNumber === 'string' && item.type !== 'promotion') {
          allONumbers.push(productNumber);
        }
      }
    }

    // Deduplicate
    const uniqueONumbers = [...new Set(allONumbers)];

    if (uniqueONumbers.length === 0) {
      setDetailsMap({});
      setIsLoadingDetails(false);
      return;
    }

    let isCancelled = false;

    const loadDetails = async () => {
      setIsLoadingDetails(true);
      setDetailsMap({}); // Clear stale data before fetching
      setHasDetailsError(false);
      try {
        const result = await fetchPacksizeDetails(uniqueONumbers);
        if (!isCancelled) {
          setDetailsMap(result.details ?? {});
          setHasDetailsError(false);
        }
      } catch (error) {
        console.error('[OrdersContext] Failed to fetch packsize details:', error);
        if (!isCancelled) {
          setDetailsMap({});
          setHasDetailsError(true);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingDetails(false);
        }
      }
    };

    loadDetails();

    return () => {
      isCancelled = true;
    };
  }, [shopwareOrders]);

  // Helper: Get total points for an order
  const getOrderTotalPoints = useCallback(
    (order: ShopwareOrder): number => {
      const lineItems = order.lineItems || [];
      let total = 0;
      for (const item of lineItems) {
        if (item.type === 'promotion') continue;
        const productNumber = item.payload?.productNumber;
        if (productNumber && typeof productNumber === 'string') {
          const details = detailsMap[productNumber];
          if (details?.points !== null && details?.points !== undefined) {
            total += details.points * (item.quantity || 1);
          }
        }
      }
      return total;
    },
    [detailsMap]
  );

  // Helper: Get packsize details for a line item
  const getLineItemDetails = useCallback(
    (oNumber: string): PacksizeDetails | null => {
      return detailsMap[oNumber] ?? null;
    },
    [detailsMap]
  );

  // Computed values
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const hasActiveFilters = dateRange.length > 0;

  // Validate currentPage against totalPages after data is loaded
  // If currentPage exceeds totalPages (e.g., from a stale bookmark), reset to last valid page
  useEffect(() => {
    if (!isLoadingOrders && totalPages > 0 && currentPage > totalPages) {
      console.warn(
        `[OrdersContext] currentPage (${currentPage}) exceeds totalPages (${totalPages}), resetting to page ${totalPages}`
      );
      setCurrentPage(totalPages);
      const url = new URL(window.location.href);
      url.searchParams.set('page', totalPages.toString());
      window.history.replaceState({}, '', url.toString());
    }
  }, [currentPage, totalPages, isLoadingOrders]);

  // Handlers
  const handleSortChange = (newSort: string) => {
    setSortOrder(newSort);
    setCurrentPage(1); // Reset to first page when sorting changes
    const url = new URL(window.location.href);
    url.searchParams.set('sort', newSort);
    url.searchParams.set('page', '1');
    window.history.pushState({}, '', url.toString());
  };

  const handleDateRangeChange = (range: string) => {
    setDateRange(range);
    setCurrentPage(1); // Reset to first page when filters change
    const url = new URL(window.location.href);
    if (range) {
      url.searchParams.set('dateRange', range);
    } else {
      url.searchParams.delete('dateRange');
    }
    url.searchParams.set('page', '1');
    window.history.pushState({}, '', url.toString());
  };

  const handleResetFilters = () => {
    setDateRange('');
    setCurrentPage(1); // Reset to first page when filters reset
    const url = new URL(window.location.href);
    url.searchParams.delete('dateRange');
    url.searchParams.set('page', '1');
    window.history.pushState({}, '', url.toString());
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    window.history.pushState({}, '', url.toString());
    // Scroll to top of orders list
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const value: OrdersContextValue = {
    // Data
    orders: shopwareOrders,
    dateRangeOptions,

    // Loading states
    isLoading: isLoadingOrders,
    hasError,

    // Pagination
    currentPage,
    totalPages,
    totalCount,
    itemsPerPage: ITEMS_PER_PAGE,

    // Filters & Sort
    sortOrder,
    dateRange,
    hasActiveFilters,

    // Packsize details
    detailsMap,
    isLoadingDetails,
    hasDetailsError,
    getOrderTotalPoints,
    getLineItemDetails,

    // Actions
    handleSortChange,
    handleDateRangeChange,
    handleResetFilters,
    handlePageChange,
  };

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>;
};

// Consumer hook
export const useOrders = (): OrdersContextValue => {
  const context = useContext(OrdersContext);
  if (!context) {
    throw new Error('useOrders must be used within an OrdersProvider');
  }
  return context;
};
